<template>
  <div class="fiscal-config-form" data-testid="fiscal-config-form">
    <box type="primary" :title="t('País e provedor')">
      <select-list
        elem-id="fiscal_config_country"
        data-testid="fiscal-config-country"
        :label="t('País')"
        :itens-select="countryOptions"
        :auto-select-first="false"
        :disabled="isLoading"
        v-model="formData.country" />

      <select-list
        elem-id="fiscal_config_invoice_provider"
        data-testid="fiscal-config-provider"
        :label="t('Provedor')"
        :itens-select="providerOptions"
        :auto-select-first="false"
        :disabled="isLoading"
        v-model="formData.invoice_provider" />

      <select-list
        v-if="formData.country === 'BR'"
        elem-id="fiscal_config_ambiente"
        data-testid="fiscal-config-ambiente"
        :label="t('Ambiente')"
        :itens-select="ambienteOptions"
        :auto-select-first="false"
        :disabled="isLoading"
        v-model="formData.ambiente" />
    </box>

    <!-- Chile DTE fields -->
    <template v-if="formData.country === 'CL'">
      <box type="primary" :title="t('Dados da empresa (Chile)')">
        <input-text
          elem-id="fiscal_config_rut_empresa"
          data-testid="fiscal-config-rut"
          :label="t('RUT da Empresa')"
          v-model="formData.rut_empresa" />
        <input-text
          elem-id="fiscal_config_razon_social"
          data-testid="fiscal-config-razon-social"
          :label="t('Razão Social')"
          v-model="formData.razon_social" />
        <input-text
          elem-id="fiscal_config_giro"
          data-testid="fiscal-config-giro"
          :label="t('Giro Comercial')"
          v-model="formData.giro" />
        <input-text
          elem-id="fiscal_config_direccion"
          data-testid="fiscal-config-direccion"
          :label="t('Endereço')"
          v-model="formData.direccion" />
        <input-text
          elem-id="fiscal_config_comuna"
          data-testid="fiscal-config-comuna"
          :label="t('Comuna')"
          v-model="formData.comuna" />
        <input-text
          elem-id="fiscal_config_ciudad"
          data-testid="fiscal-config-ciudad"
          :label="t('Cidade')"
          v-model="formData.ciudad" />
        <input-write-only-secret
          elem-id="fiscal-config-clave-sii"
          data-testid="fiscal-config-clave-sii"
          :label="t('Senha SII')"
          :has-stored-secret="hasClaveSii"
          :status-label="t('Senha cadastrada')"
          :replace-label="t('Substituir senha')"
          :cancel-label="t('Cancelar')"
          :placeholder="t('Digite a senha do SII')"
          v-model="formData.clave_sii" />
      </box>
    </template>

    <!-- Brazil NFS-e fields -->
    <template v-else-if="formData.country === 'BR'">
      <box type="primary" :title="t('Dados do emissor')">
        <input-document
          elem-id="fiscal_config_cnpj"
          data-testid="fiscal-config-cnpj"
          document-type="cnpj"
          :label="t('CNPJ')"
          :show-country-flag="false"
          v-model="formData.cnpj" />
        <input-text
          elem-id="fiscal_config_razao_social"
          data-testid="fiscal-config-razao-social"
          :label="t('Razão Social')"
          v-model="formData.razao_social" />
        <input-text
          elem-id="fiscal_config_inscricao_municipal"
          data-testid="fiscal-config-inscricao-municipal"
          :label="t('Inscrição Municipal')"
          v-model="formData.inscricao_municipal" />
        <input-text
          elem-id="fiscal_config_inscricao_estadual"
          data-testid="fiscal-config-inscricao-estadual"
          :label="t('Inscrição Estadual')"
          v-model="formData.inscricao_estadual" />
        <input-text
          elem-id="fiscal_config_cnae"
          data-testid="fiscal-config-cnae"
          :label="t('CNAE')"
          v-model="formData.cnae" />
      </box>

      <box type="primary" :title="t('Município')">
        <input-text
          elem-id="fiscal_config_cep"
          data-testid="fiscal-config-cep"
          :label="t('CEP (para preencher o município)')"
          v-model="cep" />
        <span v-if="municipioNome" data-testid="fiscal-config-municipio-nome" class="municipio-nome">
          {{ municipioNome }}
        </span>
        <small v-if="cepErro" data-testid="fiscal-config-cep-erro" class="cep-erro">
          {{ cepErro }}
        </small>
        <input-text
          elem-id="fiscal_config_municipio_ibge"
          data-testid="fiscal-config-municipio-ibge"
          :label="t('Município (código IBGE)')"
          v-model="formData.municipio_ibge" />
      </box>

      <box type="primary" :title="t('Numeração NFS-e')">
        <input-text
          elem-id="fiscal_config_serie_nfse"
          data-testid="fiscal-config-serie-nfse"
          :label="t('Série da NFS-e')"
          v-model="formData.serie_nfse" />
        <input-number
          elem-id="fiscal_config_proximo_numero_nfse"
          data-testid="fiscal-config-proximo-numero-nfse"
          :label="t('Próximo número da NFS-e')"
          :min="1"
          v-model="formData.proximo_numero_nfse" />
      </box>

      <box type="primary" :title="t('Regime')">
        <input-toggle
          elem-id="fiscal_config_optante_simples_nacional"
          data-testid="fiscal-config-optante-simples"
          :label="t('Optante do Simples Nacional')"
          v-model="formData.optante_simples_nacional" />
        <input-number
          elem-id="fiscal_config_regime_especial_tributacao"
          data-testid="fiscal-config-regime-especial-tributacao"
          :label="t('Regime especial de tributação (padrão NFS-e Nacional)')"
          :min="0"
          :max="9"
          v-model="formData.regime_especial_tributacao" />
        <small class="regime-especial-hint">
          {{ t('Só é exigido se o município desta franquia usar o padrão NFS-e Nacional. Consulte a documentação do Focus NFe para o código correto (0 = Nenhum; valores válidos: 0 a 6, ou 9).') }}
        </small>
        <input-number
          elem-id="fiscal_config_regime_especial_tributacao_municipal"
          data-testid="fiscal-config-regime-especial-tributacao-municipal"
          :label="t('Regime especial de tributação (padrão municipal)')"
          :min="0"
          :max="9"
          v-model="formData.regime_especial_tributacao_municipal" />
        <small class="regime-especial-hint">
          {{ t('Opcional, e independente do campo acima: vale para os municípios que usam o padrão MUNICIPAL do Focus NFe. O mesmo conceito tem código diferente em cada padrão, por isso os dois campos existem separados. Consulte a documentação do Focus NFe para o código correto (valores válidos: 0 a 9).') }}
        </small>
        <input-number
          elem-id="fiscal_config_codigo_opcao_simples_nacional"
          data-testid="fiscal-config-codigo-opcao-simples-nacional"
          :label="t('Código de opção pelo Simples Nacional (padrão NFS-e Nacional)')"
          :min="1"
          :max="3"
          v-model="formData.codigo_opcao_simples_nacional" />
        <small class="regime-especial-hint">
          {{ t('Só é exigido se o município desta franquia usar o padrão NFS-e Nacional. Consulte a documentação do Focus NFe para o código correto (valores válidos: 1 a 3).') }}
        </small>
        <input-number
          elem-id="fiscal_config_tributacao_iss"
          data-testid="fiscal-config-tributacao-iss"
          :label="t('Tributação do ISS (padrão NFS-e Nacional)')"
          :min="1"
          :max="4"
          v-model="formData.tributacao_iss" />
        <small class="regime-especial-hint">
          {{ t('Só é exigido se o município desta franquia usar o padrão NFS-e Nacional. Consulte a documentação do Focus NFe para o código correto (valores válidos: 1 a 4).') }}
        </small>
        <input-number
          elem-id="fiscal_config_regime_tributario_simples_nacional"
          data-testid="fiscal-config-regime-tributario-simples-nacional"
          :label="t('Regime tributário do Simples Nacional (padrão NFS-e Nacional)')"
          :min="1"
          :max="3"
          v-model="formData.regime_tributario_simples_nacional" />
        <small class="regime-especial-hint">
          {{ t('Obrigatório quando o Código de opção pelo Simples Nacional acima for 3 (ME/EPP). Consulte a documentação do Focus NFe para o código correto (valores válidos: 1 a 3).') }}
        </small>
      </box>

      <box type="primary" :title="t('Percentuais de tributos e PIS/COFINS')">
        <input-number
          elem-id="fiscal_config_percentual_total_tributos_federais"
          data-testid="fiscal-config-percentual-total-tributos-federais"
          :label="t('Percentual total de tributos federais')"
          mode="decimal"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :min="0"
          :max="100"
          suffix=" %"
          fluid
          v-model="formData.percentual_total_tributos_federais" />
        <small class="regime-especial-hint">
          {{ t('Opcional. Percentual da carga tributária federal aproximada desta franquia, informado ao tomador na nota (Lei da Transparência Fiscal).') }}
        </small>
        <input-number
          elem-id="fiscal_config_percentual_total_tributos_estaduais"
          data-testid="fiscal-config-percentual-total-tributos-estaduais"
          :label="t('Percentual total de tributos estaduais')"
          mode="decimal"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :min="0"
          :max="100"
          suffix=" %"
          fluid
          v-model="formData.percentual_total_tributos_estaduais" />
        <small class="regime-especial-hint">
          {{ t('Opcional. Zero é um valor válido e explícito — deixe em branco só se esta franquia não usa o campo.') }}
        </small>
        <input-number
          elem-id="fiscal_config_percentual_total_tributos_municipais"
          data-testid="fiscal-config-percentual-total-tributos-municipais"
          :label="t('Percentual total de tributos municipais')"
          mode="decimal"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :min="0"
          :max="100"
          suffix=" %"
          fluid
          v-model="formData.percentual_total_tributos_municipais" />
        <small class="regime-especial-hint">
          {{ t('Opcional. Zero é um valor válido e explícito — deixe em branco só se esta franquia não usa o campo.') }}
        </small>
        <input-number
          elem-id="fiscal_config_percentual_total_tributos_simples_nacional"
          data-testid="fiscal-config-percentual-total-tributos-simples-nacional"
          :label="t('Percentual total de tributos do Simples Nacional')"
          mode="decimal"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :min="0"
          :max="100"
          suffix=" %"
          fluid
          v-model="formData.percentual_total_tributos_simples_nacional" />
        <small class="regime-especial-hint">
          {{ t('Opcional. Percentual aplicado quando esta franquia é optante do Simples Nacional.') }}
        </small>
        <input-text
          elem-id="fiscal_config_situacao_tributacao_pis_cofins"
          data-testid="fiscal-config-situacao-tributacao-pis-cofins"
          :label="t('Situação da tributação de PIS/COFINS')"
          v-model="formData.situacao_tributacao_pis_cofins" />
        <small class="regime-especial-hint">
          {{ t('Opcional. Código de duas posições, digitado com o zero à esquerda (ex.: 00). Consulte a documentação do Focus NFe para o código correto.') }}
        </small>
      </box>

      <box type="primary" :title="t('Credencial Focus NFe')">
        <input-write-only-secret
          elem-id="fiscal-config-focus-token"
          data-testid="fiscal-config-focus-token"
          :label="t('Token Focus NFe')"
          :has-stored-secret="hasFocusToken"
          :status-label="t('Token cadastrado')"
          :replace-label="t('Substituir token')"
          :cancel-label="t('Cancelar')"
          :placeholder="t('Cole o token da Focus NFe')"
          v-model="focusToken" />
      </box>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { usePaymentInvoices } from '../../../composables/usePaymentInvoices';
import { useViaCep } from '../../../composables/useViaCep';
import { validateCNPJ } from '../../../utils/documentValidation';

const props = defineProps<{
  franchiseId: number;
}>();

const emit = defineEmits<{
  'save-success': [];
}>();

const { t } = useI18n();
const { getFiscalConfig, saveFiscalConfig } = usePaymentInvoices();
const { lookupCep } = useViaCep();

const isLoading = ref<boolean>(false);

// Note: API key (chile_baseapi) and sandbox_mode are NOT collected here.
// The API key is app-wide (env CHILE_BASEAPI_API_KEY) and sandbox is decided
// by the backend deployment, not the franchise admin.
const formData = reactive<{
  country: string;
  invoice_provider: string;
  // Chile
  rut_empresa: string;
  razon_social: string;
  giro: string;
  direccion: string;
  comuna: string;
  ciudad: string;
  clave_sii: string;
  // Brazil — the 12 fields the backend's brFiscalConfigShape whitelist reads/writes.
  cnpj: string;
  razao_social: string;
  inscricao_municipal: string;
  inscricao_estadual: string;
  cnae: string;
  municipio_ibge: string;
  serie_nfse: string;
  proximo_numero_nfse: number | null;
  ambiente: string;
  optante_simples_nacional: boolean;
  // NFS-e padrao NACIONAL only (E16) — presence-aware on save, like
  // optante_simples_nacional (0 is a legitimate, explicit value).
  regime_especial_tributacao: number | null;
  // NFS-e padrao NACIONAL only (E16 review, ACHADO 3) — same presence-aware
  // treatment as regime_especial_tributacao above.
  codigo_opcao_simples_nacional: number | null;
  tributacao_iss: number | null;
  // NFS-e padrao NACIONAL only, CONDITIONALLY required when
  // codigo_opcao_simples_nacional === 3 (ME/EPP) — erro real E0166. Same
  // presence-aware treatment as the other three nacional-only fields; the
  // conditional requirement itself is enforced by the backend
  // (InvoiceEmitterProfile::missingRequiredNacionalFields()), not here.
  regime_tributario_simples_nacional: number | null;
  // Migracao Zoho E1 — propriedade do EMISSOR, constante por franquia. Isto e
  // ARMAZENAMENTO: nenhum builder de payload le estes campos nesta etapa (a
  // decisao fiscal sobre o bloco `trib` do padrao nacional segue em aberto).
  // Todos required:false, todos presence-aware no save: 0 e valor REAL aqui
  // (estaduais = 0 em 14 das 21 unidades, municipais = 0 em 12), entao `null`
  // = "nunca tocado" e a unica forma de distinguir os dois casos.
  percentual_total_tributos_federais: number | null;
  percentual_total_tributos_estaduais: number | null;
  percentual_total_tributos_municipais: number | null;
  percentual_total_tributos_simples_nacional: number | null;
  // String, NAO number: o valor real e "00" e um campo numerico faria
  // Number('00') === 0, destruindo o zero a esquerda no JSON do save.
  situacao_tributacao_pis_cofins: string;
  // Chave PROPRIA: regime_especial_tributacao (acima) e a versao NACIONAL do
  // mesmo conceito, e o Focus usa valores diferentes por endpoint.
  regime_especial_tributacao_municipal: number | null;
}>({
  country: 'CL',
  invoice_provider: 'chile_baseapi',
  rut_empresa: '',
  razon_social: '',
  giro: '',
  direccion: '',
  comuna: '',
  ciudad: '',
  clave_sii: '',
  cnpj: '',
  razao_social: '',
  inscricao_municipal: '',
  inscricao_estadual: '',
  cnae: '',
  municipio_ibge: '',
  serie_nfse: '',
  proximo_numero_nfse: null,
  ambiente: 'homologacao',
  optante_simples_nacional: false,
  regime_especial_tributacao: null,
  codigo_opcao_simples_nacional: null,
  tributacao_iss: null,
  regime_tributario_simples_nacional: null,
  percentual_total_tributos_federais: null,
  percentual_total_tributos_estaduais: null,
  percentual_total_tributos_municipais: null,
  percentual_total_tributos_simples_nacional: null,
  situacao_tributacao_pis_cofins: '',
  regime_especial_tributacao_municipal: null,
});

// Non-persisted state — none of these are part of the backend whitelist.
const cep = ref<string>('');
const municipioNome = ref<string>('');
const cepErro = ref<string>('');

// The Focus NFe token: WRITE-ONLY, NEVER hydrated from the API response (see
// InputWriteOnlySecret.vue's invariant — this is task #64's failure, inverted
// into a guarantee). `hasFocusToken` is the only signal the GET carries.
const focusToken = ref<string>('');
const hasFocusToken = ref<boolean>(false);

// A senha do SII do Chile segue exatamente a mesma regra do token acima: o GET
// devolve APENAS `has_clave_sii` (#62 — antes ela voltava em TEXTO PURO pelos
// endpoints de subconta). Sem este sinal o campo abre vazio e parece que nada
// foi cadastrado, que é a origem da #64.
const hasClaveSii = ref<boolean>(false);

const countryOptions = computed(() => [
  { ID: 'CL', Name: t('Chile') },
  { ID: 'BR', Name: t('Brasil') },
]);

// The BR provider is always focus_nfe (config/invoicing.php:35) — offering
// 'nfse_brasil' here (as the old form did) persists a value that later fails
// InvoiceEmitterProfile's provider-scoped token check.
const providerOptions = computed(() => (
  formData.country === 'BR'
    ? [{ ID: 'focus_nfe', Name: t('Focus NFe (Brasil)') }]
    : [
        { ID: 'chile_baseapi', Name: t('Chile BaseAPI') },
        { ID: 'contifico', Name: t('Contifico') },
      ]
));

const ambienteOptions = computed(() => [
  { ID: 'homologacao', Name: t('Homologação') },
  { ID: 'producao', Name: t('Produção') },
]);

watch(
  () => formData.country,
  (newCountry) => {
    const validProviders = providerOptions.value.map((p) => p.ID);
    if (!validProviders.includes(formData.invoice_provider)) {
      formData.invoice_provider = newCountry === 'BR' ? 'focus_nfe' : 'chile_baseapi';
    }
  }
);

// Reactive to the current value (not just a blur event) so a franchise
// loaded with an already-valid CNPJ doesn't need a re-blur to unlock save.
const cnpjValid = computed<boolean>(() => {
  const digits = formData.cnpj.replace(/\D/g, '');
  return digits.length > 0 && validateCNPJ(formData.cnpj, t).valid;
});

const canSave = computed<boolean>(() => {
  if (!props.franchiseId) return false;
  if (formData.country === 'CL') {
    return !!(formData.rut_empresa.trim() && formData.razon_social.trim());
  }
  if (formData.country === 'BR') {
    return !!(
      cnpjValid.value &&
      formData.razao_social.trim() &&
      formData.inscricao_municipal.trim() &&
      formData.municipio_ibge.trim() &&
      (hasFocusToken.value || focusToken.value.trim())
    );
  }
  return false;
});

// Triggered when the CEP reaches 8 digits (not on every keystroke, not on
// mount/hydration) — a Vue-level "@blur" on the CEP input wrapper would
// silently never fire: the wrapper doesn't declare "blur" as an emit, so the
// listener falls through onto its OUTER wrapper div, and native blur doesn't
// bubble from the inner input up to it.
watch(cep, async (newCep) => {
  const digits = newCep.replace(/\D/g, '');
  if (digits.length !== 8) return;
  const r = await lookupCep(newCep);
  if (r.erro) {
    cepErro.value = t('CEP não encontrado — informe o código IBGE manualmente');
    municipioNome.value = '';
    return;
  }
  cepErro.value = '';
  formData.municipio_ibge = r.ibge || formData.municipio_ibge;
  municipioNome.value = r.localidade ? `${r.localidade}/${r.uf}` : '';
});

const resetForm = (): void => {
  formData.country = 'CL';
  formData.invoice_provider = 'chile_baseapi';
  formData.rut_empresa = '';
  formData.razon_social = '';
  formData.giro = '';
  formData.direccion = '';
  formData.comuna = '';
  formData.ciudad = '';
  formData.clave_sii = '';
  formData.cnpj = '';
  formData.razao_social = '';
  formData.inscricao_municipal = '';
  formData.inscricao_estadual = '';
  formData.cnae = '';
  formData.municipio_ibge = '';
  formData.serie_nfse = '';
  formData.proximo_numero_nfse = null;
  formData.ambiente = 'homologacao';
  formData.optante_simples_nacional = false;
  formData.regime_especial_tributacao = null;
  formData.codigo_opcao_simples_nacional = null;
  formData.tributacao_iss = null;
  formData.regime_tributario_simples_nacional = null;
  formData.percentual_total_tributos_federais = null;
  formData.percentual_total_tributos_estaduais = null;
  formData.percentual_total_tributos_municipais = null;
  formData.percentual_total_tributos_simples_nacional = null;
  formData.situacao_tributacao_pis_cofins = '';
  formData.regime_especial_tributacao_municipal = null;
  cep.value = '';
  municipioNome.value = '';
  cepErro.value = '';
  focusToken.value = '';
  hasFocusToken.value = false;
  hasClaveSii.value = false;
};

const loadForFranchise = async (id: number): Promise<void> => {
  log('FiscalConfigForm', 'loadForFranchise', 'Loading', id);
  isLoading.value = true;
  try {
    const dados = await getFiscalConfig(id);
    if (dados?.config) {
      const c = dados.config;
      formData.country = (c.country as string) || 'CL';
      if (formData.country === 'BR') {
        formData.invoice_provider = (c.invoice_provider as string) || 'focus_nfe';
        formData.cnpj = (c.cnpj as string) || '';
        formData.razao_social = (c.razao_social as string) || '';
        formData.inscricao_municipal = (c.inscricao_municipal as string) || '';
        formData.inscricao_estadual = (c.inscricao_estadual as string) || '';
        formData.cnae = (c.cnae as string) || '';
        formData.municipio_ibge = (c.municipio_ibge as string) || '';
        formData.serie_nfse = (c.serie_nfse as string) || '';
        formData.proximo_numero_nfse = (c.proximo_numero_nfse as number | null) ?? null;
        formData.ambiente = (c.ambiente as string) || 'homologacao';
        formData.optante_simples_nacional = !!c.optante_simples_nacional;
        formData.regime_especial_tributacao =
          c.regime_especial_tributacao != null ? Number(c.regime_especial_tributacao) : null;
        formData.codigo_opcao_simples_nacional =
          c.codigo_opcao_simples_nacional != null ? Number(c.codigo_opcao_simples_nacional) : null;
        formData.tributacao_iss =
          c.tributacao_iss != null ? Number(c.tributacao_iss) : null;
        formData.regime_tributario_simples_nacional =
          c.regime_tributario_simples_nacional != null ? Number(c.regime_tributario_simples_nacional) : null;
        // Migracao Zoho E1 — mesmo `!= null` (e nao truthiness) dos campos
        // acima: e o que faz um 0 vindo do banco reaparecer no campo em vez de
        // virar "nunca configurado". Sem esta hidratacao o campo seria
        // gravavel mas nao EDITAVEL: o usuario reabriria a tela em branco.
        formData.percentual_total_tributos_federais =
          c.percentual_total_tributos_federais != null ? Number(c.percentual_total_tributos_federais) : null;
        formData.percentual_total_tributos_estaduais =
          c.percentual_total_tributos_estaduais != null ? Number(c.percentual_total_tributos_estaduais) : null;
        formData.percentual_total_tributos_municipais =
          c.percentual_total_tributos_municipais != null ? Number(c.percentual_total_tributos_municipais) : null;
        formData.percentual_total_tributos_simples_nacional =
          c.percentual_total_tributos_simples_nacional != null
            ? Number(c.percentual_total_tributos_simples_nacional)
            : null;
        // String: Number() aqui destruiria o zero a esquerda do "00".
        formData.situacao_tributacao_pis_cofins = (c.situacao_tributacao_pis_cofins as string) || '';
        formData.regime_especial_tributacao_municipal =
          c.regime_especial_tributacao_municipal != null
            ? Number(c.regime_especial_tributacao_municipal)
            : null;
        // Only the presence flag is read here — see the file-level comment
        // above `focusToken`. Never: formData/focusToken assigned from `c`.
        hasFocusToken.value = !!c.has_focus_nfe_token;
      } else {
        formData.invoice_provider = (c.invoice_provider as string) || 'chile_baseapi';
        formData.rut_empresa = (c.rut_empresa as string) || '';
        formData.razon_social = (c.razon_social as string) || '';
        formData.giro = (c.giro as string) || '';
        formData.direccion = (c.direccion as string) || '';
        formData.comuna = (c.comuna as string) || '';
        formData.ciudad = (c.ciudad as string) || '';
        // clave_sii is never hydrated — the backend never returns it either.
        // Only the presence flag, same rule as hasFocusToken above (#62).
        hasClaveSii.value = !!c.has_clave_sii;
      }
    } else {
      resetForm();
    }
  } finally {
    isLoading.value = false;
  }
};

const saveFiscalConfigAction = async (): Promise<void> => {
  log('FiscalConfigForm', 'saveFiscalConfig', 'Saving', { franchiseId: props.franchiseId, formData });
  if (!canSave.value) {
    eventBus.emit('globalMessage.warning', t('Preencha os campos obrigatórios'));
    return;
  }
  try {
    const config = formData.country === 'BR'
      ? {
          country: 'BR',
          invoice_provider: 'focus_nfe',
          cnpj: formData.cnpj,
          razao_social: formData.razao_social,
          inscricao_municipal: formData.inscricao_municipal,
          inscricao_estadual: formData.inscricao_estadual,
          cnae: formData.cnae,
          municipio_ibge: formData.municipio_ibge,
          serie_nfse: formData.serie_nfse,
          proximo_numero_nfse: formData.proximo_numero_nfse ?? '',
          ambiente: formData.ambiente,
          // Always present, including `false` — the backend's presence check
          // (array_key_exists) only works if we never omit it.
          optante_simples_nacional: formData.optante_simples_nacional,
          // NFS-e padrao NACIONAL only (E16) — presence-aware like
          // optante_simples_nacional above (0 is a legitimate, explicit
          // value: "Nenhum regime especial"). Omitted when the field was
          // never touched (null) so a franchise that never needed it never
          // gets a phantom 0 written — the backend's own array_key_exists
          // preserve-on-omission rule (resolveRegimeEspecialTributacaoForSave())
          // depends on this.
          ...(formData.regime_especial_tributacao !== null
            ? { regime_especial_tributacao: formData.regime_especial_tributacao }
            : {}),
          // Same presence-aware omission as regime_especial_tributacao
          // above (E16 review, ACHADO 3).
          ...(formData.codigo_opcao_simples_nacional !== null
            ? { codigo_opcao_simples_nacional: formData.codigo_opcao_simples_nacional }
            : {}),
          ...(formData.tributacao_iss !== null
            ? { tributacao_iss: formData.tributacao_iss }
            : {}),
          // Same presence-aware omission as the three fields above —
          // CONDITIONALLY required (only when codigo_opcao_simples_nacional
          // above is 3, ME/EPP), but the frontend never enforces that
          // condition itself; it only omits when the user never touched it.
          ...(formData.regime_tributario_simples_nacional !== null
            ? { regime_tributario_simples_nacional: formData.regime_tributario_simples_nacional }
            : {}),
          // Migracao Zoho E1 — os 6 campos fiscais do emissor. `!== null`,
          // NUNCA truthiness: `formData.x ? ... : {}` engoliria o 0, que e o
          // valor real de percentual_total_tributos_estaduais em 14 das 21
          // unidades e de _municipais em 12 — a franquia perderia o campo em
          // silencio. A omissao-quando-null e o que casa com o PUT parcial
          // (preserve-on-omission), igual aos quatro campos nacionais acima.
          ...(formData.percentual_total_tributos_federais !== null
            ? { percentual_total_tributos_federais: formData.percentual_total_tributos_federais }
            : {}),
          ...(formData.percentual_total_tributos_estaduais !== null
            ? { percentual_total_tributos_estaduais: formData.percentual_total_tributos_estaduais }
            : {}),
          ...(formData.percentual_total_tributos_municipais !== null
            ? { percentual_total_tributos_municipais: formData.percentual_total_tributos_municipais }
            : {}),
          ...(formData.percentual_total_tributos_simples_nacional !== null
            ? { percentual_total_tributos_simples_nacional: formData.percentual_total_tributos_simples_nacional }
            : {}),
          // String: omitida quando vazia (mesma regra do focus_nfe_token
          // abaixo). O valor viaja como digitado — "00" continua "00".
          ...(formData.situacao_tributacao_pis_cofins.trim()
            ? { situacao_tributacao_pis_cofins: formData.situacao_tributacao_pis_cofins.trim() }
            : {}),
          ...(formData.regime_especial_tributacao_municipal !== null
            ? { regime_especial_tributacao_municipal: formData.regime_especial_tributacao_municipal }
            : {}),
          // Omitted entirely when the user didn't type one — see
          // resolveFocusTokenForSave() on the backend: a missing key
          // preserves the existing token; an empty string does too, but
          // omission is the explicit contract.
          ...(focusToken.value.trim() ? { focus_nfe_token: focusToken.value.trim() } : {}),
        }
      : {
          country: formData.country,
          invoice_provider: formData.invoice_provider,
          rut_empresa: formData.rut_empresa,
          razon_social: formData.razon_social,
          giro: formData.giro,
          direccion: formData.direccion,
          comuna: formData.comuna,
          ciudad: formData.ciudad,
          clave_sii: formData.clave_sii,
        };
    await saveFiscalConfig(props.franchiseId, { config: JSON.stringify(config) });
    eventBus.emit('globalMessage.success', t('Configuração fiscal salva'));
    emit('save-success');
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('FiscalConfigForm', 'saveFiscalConfig', 'Error', err);
    eventBus.emit('globalMessage.error', message || t('Erro ao salvar configuração fiscal'));
  }
};

defineExpose({
  canSave,
  loadForFranchise,
  saveFiscalConfig: saveFiscalConfigAction,
  resetForm,
});
</script>

<style scoped>
.fiscal-config-form :deep(.municipio-nome) {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.875rem;
}
.fiscal-config-form :deep(.cep-erro) {
  color: var(--p-red-500, #ef4444);
  display: block;
}
.fiscal-config-form :deep(.regime-especial-hint) {
  display: block;
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}
</style>
